import React, { useState, useCallback } from 'react';
import { Upload, Button, message, Progress, Card, Space, Typography, Modal, Image, Row, Col } from 'antd';
import { InboxOutlined, CheckCircleOutlined, LoadingOutlined, EyeOutlined } from '@ant-design/icons';
import { useAutoRecognition } from '../hooks/useAutoRecognition';
import { paymentScreenshotAPI } from '../services/paymentScreenshotAPI';

const { Dragger } = Upload;
const { Title, Text } = Typography;

interface PaymentUploadProps {
  courseId: string;
  onUploadComplete?: (result: any) => void;
  onRecognitionComplete?: (result: any) => void;
  showProgress?: boolean;
}

/**
 * 支付截图上传组件 - 集成自动识别功能
 * 上传完成后自动触发OCR识别
 */
export const PaymentUploadWithAutoRecognition: React.FC<PaymentUploadProps> = ({
  courseId,
  onUploadComplete,
  onRecognitionComplete,
  showProgress = true
}) => {
  const { uploadAndTriggerRecognition, status, resetStatus } = useAutoRecognition();
  const [uploading, setUploading] = useState(false);
  const [showExampleModal, setShowExampleModal] = useState(false);

  /**
   * 处理文件上传和自动识别
   */
  const handleUpload = useCallback(async (file: File) => {
    if (!file) return;

    // 文件验证
    if (!file.type.startsWith('image/')) {
      message.error('请上传图片文件');
      return false;
    }

    if (file.size / 1024 / 1024 > 5) {
      message.error('图片大小不能超过5MB');
      return false;
    }

    setUploading(true);

    try {
      // 使用增强的自动识别上传
      const result = await uploadAndTriggerRecognition(file, courseId, (progress) => {
        console.log('Upload progress:', progress);
      });

      console.log('Upload result:', result);

      if (result.success) {
        // 检查是否有详细的验证结果
        if (result.ocrResult?.autoApproved) {
          message.success('🎉 支付验证成功，报名已完成！');
        } else if (result.ocrResult) {
          message.info('截图已上传，正在等待管理员审核...');
        } else {
          message.success(result.message || '上传成功，识别完成');
        }
        onUploadComplete?.(result);
        onRecognitionComplete?.(result);
      } else {
        // 处理验证失败的情况
        console.log('🚨 验证失败:', result);
        // 错误已经在状态里设置，不需要额外处理
      }
    } catch (error) {
      console.error('Upload error:', error);
      // 错误处理已经在 hook 中完成
    } finally {
      setUploading(false);
    }

    return false; // 阻止自动上传
  }, [courseId, uploadAndTriggerRecognition, onUploadComplete, onRecognitionComplete]);

  const uploadProps = {
    name: 'screenshot',
    accept: 'image/*',
    multiple: false,
    showUploadList: false,
    beforeUpload: handleUpload,
    disabled: uploading || status.isProcessing
  };

  return (
    <div className="payment-upload-auto">
      <Card title="支付截图上传说明" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Image
              width="100%"
              src="/instruct1.jpg"
              alt="步骤1"
              preview={false}
              fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgdmlld0JveD0iMCAwIDIwMCAxNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTUwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjEwMCIgeT0iNzUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM4QzhDOEMiPuWPr+WGjeeUn+WIhuaekDwvdGV4dD4KPC9zdmc+"
            />
            <p style={{ textAlign: 'center', marginTop: 8 }}>
              <strong>步骤1：</strong>进入支付宝账单详情页面
            </p>
          </Col>
          <Col span={12}>
            <Image
              width="100%"
              src="/instruct2.jpg"
              alt="步骤2"
              preview={false}
              fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgdmlld0JveD0iMCAwIDIwMCAxNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTUwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjEwMCIgeT0iNzUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM4QzhDOEMiPuWPr+WGjeeUn+WIhuaekDwvdGV4dD4KPC9zdmc+"
            />
            <p style={{ textAlign: 'center', marginTop: 8 }}>
              <strong>步骤2：</strong>截图包含订单号和支付金额
            </p>
          </Col>
        </Row>
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Image
              width="100%"
              src="/instruct3.jpg"
              alt="步骤3"
              preview={false}
              fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgdmlld0JveD0iMCAwIDIwMCAxNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTUwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjEwMCIgeT0iNzUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM4QzhDOEMiPuWPr+WGjeeUn+WIhuaekDwvdGV4dD4KPC9zdmc+"
            />
            <p style={{ textAlign: 'center', marginTop: 8 }}>
              <strong>步骤3：</strong>确保截图清晰完整
            </p>
          </Col>
          <Col span={12}>
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => setShowExampleModal(true)}
              style={{ width: '100%', height: '100%' }}
            >
              查看示例截图
            </Button>
          </Col>
        </Row>
      </Card>

      <Dragger {...uploadProps}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined style={{ fontSize: 48, color: '#1890ff' }} />
        </p>
        <p className="ant-upload-text">点击或拖拽上传支付截图</p>
        <p className="ant-upload-hint">
          支持 JPG、PNG、GIF 格式，文件大小不超过5MB
        </p>
        <p className="ant-upload-hint">
          上传后将自动识别支付信息，无需手动填写
        </p>
      </Dragger>

      <Modal
        title="支付截图示例"
        visible={showExampleModal}
        onCancel={() => setShowExampleModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowExampleModal(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        <div style={{ textAlign: 'center' }}>
          <Image
            width="100%"
            src="/instruct4.jpg"
            alt="支付截图示例"
            preview={false}
            fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNzAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDcwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI3MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjM1MCIgeT0iMjAwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LXNpemU9IjE4IiBmaWxsPSIjOEM4QzhDIj7mlbDmja7liqjmgIHkvZzog73mj4Zlm54g6K6+6K6hPC90ZXh0Pgo8L3N2Zz4="
          />
          <div style={{ marginTop: 16 }}>
            <Text>
              请确保截图包含完整的订单信息：订单号、支付金额、支付时间、收款方信息
            </Text>
          </div>
        </div>
      </Modal>

      {showProgress && (uploading || status.isProcessing) && (
        <Card style={{ marginTop: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text>上传和识别进度：</Text>
              <Progress 
                percent={Math.round(status.progress)} 
                status={status.error ? 'exception' : 'normal'}
              />
            </div>
            
            {status.isProcessing && (
              <Space>
                <LoadingOutlined />
                <Text>正在识别支付信息...</Text>
              </Space>
            )}
            
            {status.result && (
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <Text type="success">识别完成</Text>
              </Space>
            )}
            
            {status.error && (
              <Text type="danger">{status.error}</Text>
            )}
          </Space>
        </Card>
      )}

 {status.error && (
        <Card style={{ marginTop: 16, borderColor: '#ff4d4f', backgroundColor: '#fff2f0' }} type="inner" title="验证结果">
          <Text type="danger" style={{ fontSize: 16, fontWeight: 'bold' }}>
            ❌ {status.error}
          </Text>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
              请确认支付金额与课程价格一致后，重新上传正确的支付截图。
            </Text>
          </div>
          <div style={{ marginTop: 16 }}>
            <Button 
              type="primary" 
              onClick={() => {
                // 重置状态，允许重新上传
                resetStatus();
                setUploading(false);
              }}
            >
              重新上传
            </Button>
          </div>
        </Card>
      )}


      {status.result?.data && (
        <Card style={{ marginTop: 16 }} title="识别结果">
          <Space direction="vertical" style={{ width: '100%' }}>
            {status.result.data.orderNumber && (
              <div>
                <Text strong>订单号：</Text>
                <Text>{status.result.data.orderNumber}</Text>
              </div>
            )}
            {status.result.data.amount && (
              <div>
                <Text strong>支付金额：</Text>
                <Text>¥{status.result.data.amount}</Text>
              </div>
            )}
            {status.result.data.paymentTime && (
              <div>
                <Text strong>支付时间：</Text>
                <Text>{status.result.data.paymentTime}</Text>
              </div>
            )}
            {status.result.data.autoApproved && (
              <div style={{ textAlign: 'center', padding: '16px' }}>
                <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 8 }} />
                <Title level={4} style={{ color: '#52c41a', margin: 0 }}>
                  🎉 报名成功！
                </Title>
                <Text type="success" style={{ display: 'block', marginTop: 8 }}>
                  支付验证成功，您已成功报名该课程
                </Text>
                <Button 
                  type="primary" 
                  style={{ marginTop: 16 }}
                  onClick={() => window.location.reload()}
                >
                  完成并刷新页面
                </Button>
              </div>
            )}
          </Space>
        </Card>
      )}
    </div>
  );
};

export default PaymentUploadWithAutoRecognition;